import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Trash2, LogOut, RefreshCw } from "lucide-react";
import AdminLinks from "@/components/site/AdminLinks";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["pending", "confirmed", "archived"];

const statusCls = {
  pending: "text-[#38bdf8] border-[#38bdf8]/40",
  confirmed: "text-emerald-300 border-emerald-300/40",
  archived: "text-[#64646e] border-white/10",
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("tone_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState(null);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("bookings");

  const logout = useCallback(() => {
    localStorage.removeItem("tone_admin_token");
    setToken("");
    setBookings(null);
  }, []);

  const load = useCallback(
    async (t) => {
      try {
        const res = await fetch(`${API}/admin/bookings`, { headers: { Authorization: `Bearer ${t || token}` } });
        if (res.status === 401) return logout();
        setBookings(await res.json());
        const s = await fetch(`${API}/admin/analytics`, { headers: { Authorization: `Bearer ${t || token}` } });
        if (s.ok) setStats(await s.json());
      } catch {
        setError("Anfragen konnten nicht geladen werden.");
      }
    },
    [token, logout]
  );

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = Array.isArray(data.detail) ? data.detail.map((d) => d.msg).join(" ") : data.detail;
        throw new Error(detail || "Login fehlgeschlagen.");
      }
      localStorage.setItem("tone_admin_token", data.access_token);
      setToken(data.access_token);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const setStatus = async (id, status) => {
    await fetch(`${API}/admin/bookings/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status }),
    });
    setBookings((b) => b.map((x) => (x.id === id ? { ...x, status } : x)));
  };

  const remove = async (id) => {
    await fetch(`${API}/admin/bookings/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    setBookings((b) => b.filter((x) => x.id !== id));
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/15 py-3.5 font-mono2 text-sm text-[#f5f3ef] placeholder:text-[#64646e] placeholder:uppercase placeholder:text-[11px] placeholder:tracking-[0.3em] focus:outline-none focus:border-[#38bdf8]/80 transition-colors duration-500";

  if (!token) {
    return (
      <main data-testid="admin-login-page" className="min-h-screen bg-[#070709] text-[#f5f3ef] flex items-center justify-center px-6">
        <div className="noise-page grain" />
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }} className="w-full max-w-sm">
          <p className="font-mono2 text-[10px] tracking-[0.4em] text-[#64646e] uppercase">TONE — ADMIN</p>
          <h1 className="font-display font-extrabold uppercase tracking-tight text-4xl mt-3">BACKSTAGE</h1>
          <form data-testid="admin-login-form" onSubmit={login} className="mt-10 space-y-7">
            <input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-MAIL" className={inputCls} />
            <input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="PASSWORT" className={inputCls} />
            <button data-testid="admin-login-submit" disabled={loading} className="w-full py-4 bg-[#f5f3ef] text-[#070709] font-display font-bold text-sm hover:bg-[#70c7fa] disabled:opacity-50 transition-all duration-500">
              {loading ? "…" : "LOGIN"}
            </button>
            {error && (
              <p data-testid="admin-login-error" className="font-mono2 text-[11px] tracking-[0.2em] text-red-400 uppercase">
                {error}
              </p>
            )}
          </form>
          <Link to="/" className="block mt-10 font-mono2 text-[10px] tracking-[0.3em] text-[#64646e] hover:text-[#70c7fa] uppercase transition-colors">
            ← Zurück zur Website
          </Link>
        </motion.div>
      </main>
    );
  }

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-[#070709] text-[#f5f3ef] px-6 md:px-12 py-10">
      <div className="noise-page grain" />
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 pb-8 border-b border-white/10">
          <div>
            <p className="font-mono2 text-[10px] tracking-[0.4em] text-[#64646e] uppercase">TONE — ADMIN</p>
            <h1 className="font-display font-extrabold uppercase tracking-tight text-3xl md:text-5xl mt-2">
              {tab === "links" ? (
                <>
                  LINK <span className="text-[#70c7fa]">IN BIO</span>
                </>
              ) : (
                <>
                  BOOKING <span className="text-[#70c7fa]">ANFRAGEN</span>
                </>
              )}
            </h1>
            <div className="flex gap-2 mt-6">
              <button
                data-testid="admin-tab-bookings"
                onClick={() => setTab("bookings")}
                className={`px-5 py-2.5 border font-mono2 text-[10px] tracking-[0.3em] uppercase transition-all duration-300 ${
                  tab === "bookings" ? "border-[#38bdf8]/60 text-[#70c7fa]" : "border-white/10 text-[#64646e] hover:text-[#a3a3ac]"
                }`}
              >
                Anfragen
              </button>
              <button
                data-testid="admin-tab-links"
                onClick={() => setTab("links")}
                className={`px-5 py-2.5 border font-mono2 text-[10px] tracking-[0.3em] uppercase transition-all duration-300 ${
                  tab === "links" ? "border-[#38bdf8]/60 text-[#70c7fa]" : "border-white/10 text-[#64646e] hover:text-[#a3a3ac]"
                }`}
              >
                Links
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh-btn" onClick={() => load()} className="p-3 border border-white/15 hover:border-[#38bdf8]/60 hover:text-[#70c7fa] transition-all" aria-label="Refresh">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-2 px-5 py-3 border border-white/15 font-mono2 text-[10px] tracking-[0.3em] hover:border-red-400/60 hover:text-red-300 transition-all uppercase">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>

        {tab === "links" ? (
          <AdminLinks token={token} />
        ) : (
          <>
            {stats && (
          <div data-testid="admin-stats" className="py-6 border-b border-white/10 flex flex-wrap items-center gap-x-8 gap-y-2 font-mono2 text-[10px] tracking-[0.2em] uppercase">
            <span className="text-[#38bdf8]">{stats.pageviews} Pageviews</span>
            <span className="text-[#a3a3ac]">{stats.clicks} Clicks</span>
            {stats.top.slice(0, 5).map((t) => (
              <span key={t.target} className="text-[#64646e]">
                {t.target}: {t.count}
              </span>
            ))}
          </div>
        )}

        {bookings === null ? (
          <p className="py-20 font-mono2 text-xs tracking-[0.3em] text-[#64646e] uppercase">Lade Anfragen…</p>
        ) : bookings.length === 0 ? (
          <p data-testid="admin-empty" className="py-20 font-mono2 text-xs tracking-[0.3em] text-[#64646e] uppercase">
            Keine Anfragen vorhanden.
          </p>
        ) : (
          <div className="divide-y divide-white/5">
            <AnimatePresence initial={false}>
              {bookings.map((b) => (
                <motion.div
                  key={b.id}
                  data-testid="admin-inquiry-row"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, height: 0 }}
                  className="py-6 grid md:grid-cols-12 gap-4 items-start"
                >
                  <div className="md:col-span-3">
                    <p className="font-display font-bold text-lg leading-tight">{b.name}</p>
                    <a href={`mailto:${b.email}`} className="font-mono2 text-[11px] text-[#38bdf8] hover:text-[#70c7fa] break-all">
                      {b.email}
                    </a>
                    <p className="font-mono2 text-[10px] tracking-[0.2em] text-[#64646e] mt-2 uppercase">
                      {new Date(b.created_at).toLocaleString("de-DE")}
                    </p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="font-mono2 text-[10px] tracking-[0.25em] text-[#a3a3ac] uppercase">{b.inquiry_type}</p>
                  </div>
                  <p className="md:col-span-4 text-sm text-[#a3a3ac] font-light leading-relaxed whitespace-pre-wrap">{b.message}</p>
                  <div className="md:col-span-3 flex flex-wrap items-center gap-2 md:justify-end">
                    {STATUSES.map((s) => (
                      <button
                        key={s}
                        data-testid={`admin-status-${s}`}
                        onClick={() => setStatus(b.id, s)}
                        className={`px-3 py-1.5 border font-mono2 text-[9px] tracking-[0.25em] uppercase transition-all duration-300 ${
                          b.status === s ? statusCls[s] : "border-white/10 text-[#64646e] hover:text-[#a3a3ac]"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                    <button data-testid="admin-delete-btn" onClick={() => remove(b.id)} className="p-2 text-[#64646e] hover:text-red-400 transition-colors" aria-label="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
          </>
        )}

        <Link to="/" className="inline-block mt-14 font-mono2 text-[10px] tracking-[0.3em] text-[#64646e] hover:text-[#70c7fa] uppercase transition-colors">
          ← Zurück zur Website
        </Link>
      </div>
    </main>
  );
}
